import Header from "../../sharedScreens/Header";
import SideMenu from "../../sharedScreens/menuItems/SideMenu";
import { FormikValues, useFormik } from "formik";
import { CreateUserSchema } from "../../validations/Schema";
import { MuiInput } from "../../components/basicComponent/MuiInput";
import { MuiDropDown } from "../../components/basicComponent/MuiDropDown";
import { useNavigate, useLocation } from "react-router-dom";
import MuiButton from "../../components/basicComponent/MuiButton";
import { UserModel } from "../../model/controlPanelModels/CreateUserModel";
import {
  roleList,
  statusList,
  titleList,
  branchList,
} from "../../constants/constant";
import useCompanyList from "../../services/customHooks/useCompanyList";
import { useEffect, useRef, useState } from "react";
import { UtilService } from "../../services/shared/UtilService";
import { msgTypes } from "../../constants/msgTypes";
import Permissions from "./Permissions";
import { useGlobalState } from "../../services/context/AuthContext";
import { capitalizeFirstLetter } from "../../constants/helper";
import { useSweetAlert } from "../../services/customHooks/useSweetAlert";
import PhoneInput from "react-phone-input-2";

const UserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useGlobalState();
  const { showError, showSuccess } = useSweetAlert();
  const [clearPermissions, setClearPermissions] = useState<boolean>();
  const editingUser = location.state?.data?.[0] || null; // Check if user data exists
  // Refs for form fields to enable scrolling and focusing
  const fieldRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Function to scroll to the first error field and focus on it
  const scrollToFirstError = (errors: Record<string, any>) => {
    const fieldOrder = [
      "title",
      "firstName",
      "lastName",
      "email",
      "mobileNumber",
      "employeeCode",
      "status",
      "feature_ids",
    ];

    const firstErrorField = fieldOrder.find((field) => errors[field]);

    if (firstErrorField) {
      const element = fieldRefs.current[firstErrorField];

      // ✅ Check if element is not null
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        setTimeout(() => {
          const inputElement = element.querySelector(
            "input, select, textarea"
          ) as HTMLElement | null;

          if (inputElement) {
            inputElement.focus();
          } else {
            element.focus(); // Also safe now
          }
        }, 300);
      }
    }
  };

  // Function to register field refs
  const setFieldRef = (fieldName: string) => (ref: HTMLElement | null) => {
    fieldRefs.current[fieldName] = ref;
  };
  const userId = editingUser?.id;
  const userData = {
    title: editingUser?.title ?? "",
    firstName: editingUser?.user?.firstName ?? "",
    lastName: editingUser?.user?.lastName ?? "",
    email: editingUser?.user?.email ?? "",
    mobileNumber: editingUser?.mobileNumber ?? "",
    employeeCode: editingUser?.employeeCode ?? "",
    status: editingUser?.status ?? "",
    feature_ids: editingUser?.user?.featurePermissions?.map(
      (i: any) => i?.feature
    ),
  };
  const formik = useFormik({
    initialValues: new UserModel(userData),
    validationSchema: CreateUserSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!!editingUser) {
        editUserRolesAndPermission(values);
      } else {
        createUser();
      }
    },
  });

  const branch = !!formik.values.branch ? formik.values.branch : {};
  const { data: corporateList, loading, error } = useCompanyList(branch);

  async function createUser() {
    const payload = {
      ...formik.values,
      firstName: capitalizeFirstLetter(formik.values.firstName),
      lastName: capitalizeFirstLetter(formik.values.lastName),
      password: "User@123",
      comfirm_password: "User@123",
    };
    try {
      const response = await UtilService.createUser(payload);

      if (response.statusMsg === msgTypes.SUCCESS.msg) {
        showSuccess("Success!", "User has been created successfully.");
        formik.resetForm();
        navigate("/userinfolist");
      } else {
        showError(
          "Oops...",
          response?.statusRemark ?? "Something went wrong while creating user!"
        );
      }
    } catch (error) {
      console.error("Error in createUser:", error);
    } finally {
    }
  }

  const getUpdatedFields = (initial: any, current: any): any => {
    const updatedFields: any = {};
    for (const key in current) {
      if (Array.isArray(current[key])) {
        if (
          JSON.stringify(current[key] || []) !==
          JSON.stringify(initial[key] || [])
        ) {
          updatedFields[key] = current[key];
        }
      } else if (current[key] !== initial[key]) {
        updatedFields[key] = current[key];
      }
    }
    return updatedFields;
  };

  const editUserRolesAndPermission = async (values: FormikValues) => {
    try {
      const initial = {
        title: editingUser?.title ?? "",
        firstName: editingUser?.user?.firstName ?? "",
        lastName: editingUser?.user?.lastName ?? "",
        email: editingUser?.user?.email ?? "",
        mobileNumber: editingUser?.mobileNumber ?? "",
        employeeCode: editingUser?.employeeCode ?? "",
        status: editingUser?.status ?? "",
        feature_ids:
          editingUser?.user?.featurePermissions?.map((i: any) => i?.feature) ??
          [],
      };

      const changes = getUpdatedFields(initial, values);

      if (Object.keys(changes).length === 0) {
        showError(
          "No changes detected",
          "Please modify at least one field before updating."
        );
        return;
      }

      const payload = {
        id: editingUser?.id, // or editingUser?.user?.id depending on backend
        ...changes,
      };

      const res = await UtilService.EditUser(payload, userId);

      if (res.statusMsg === msgTypes.SUCCESS.msg) {
        showSuccess("Success!", "User has been updated successfully.");
        navigate("/userinfolist");
      } else {
        showError("Oops", res?.statusRemark || "Failed to update user.");
      }
    } catch (error) {
      console.error("An error occurred during editUser:", error);
      showError("Unexpected error", "Please try again.");
    }
  };

  const getPermissionsId = (id: string[]) => {
    formik.setFieldValue("feature_ids", id);
  };
  const handleClear = () => {
    formik.resetForm();
    setClearPermissions(!clearPermissions);
  };

  const hasUserChanged = (values: FormikValues) => {
    const initial = {
      title: editingUser?.title ?? "",
      firstName: editingUser?.user?.firstName ?? "",
      lastName: editingUser?.user?.lastName ?? "",
      mobileNumber: editingUser?.mobileNumber ?? "",
      employeeCode: editingUser?.employeeCode ?? "",
      status: editingUser?.status ?? "",
      feature_ids:
        editingUser?.user?.featurePermissions?.map((i: any) => i?.feature) ??
        [],
    };

    return JSON.stringify(initial) !== JSON.stringify(values);
  };

  // Enhanced submit handler with scroll to error functionality
  const handleFormSubmit = async () => {
    const errors = await formik.validateForm();
    formik.setTouched({
      title: true,
      firstName: true,
      lastName: true,
      email: true,
      mobileNumber: true,
      employeeCode: true,
      status: true,
      feature_ids: true,
    });

    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
      return;
    }

    if (editingUser) {
      if (!hasUserChanged(formik.values)) {
        showError(
          "No changes detected",
          "Please modify at least one field before updating."
        );
        return;
      }
      editUserRolesAndPermission(formik.values);
    } else {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <div className="grow">
        <SideMenu />
        <div className="wrapper flex grow flex-col">
          <Header />
          <main className="grow content" id="content" role="content">
            <div className="container-fixed">
              <div className="card-header text-center">
                <h3 className="card-title">
                  {editingUser ? "Edit User" : " Corporate User Management"}
                </h3>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-custom">
                    <div className="card-body">
                      <div className="row mt-4">
                        <div
                          className="col-md-4 my-under-500"
                          ref={setFieldRef("title")}
                        >
                          <MuiDropDown
                            label="Title"
                            options={titleList}
                            placeholder="Enter Title"
                            isRequired
                            isInvalid={
                              formik.touched.title && !!formik.errors.title
                            }
                            errorText={formik.errors.title as string}
                            onChange={(value) =>
                              formik.setFieldValue("title", value)
                            }
                            onBlur={() => formik.setFieldTouched("title", true)}
                            value={formik.values.title?.toString()}
                          />
                        </div>
                        <div
                          className="col-md-4 my-under-500"
                          ref={setFieldRef("firstName")}
                        >
                          <MuiInput
                            label="First Name"
                            placeholder="Enter First Name"
                            isRequired
                            isInvalid={
                              formik.touched.firstName &&
                              !!formik.errors.firstName
                            }
                            errorText={formik.errors.firstName as string}
                            onChange={(value) =>
                              formik.setFieldValue("firstName", value)
                            }
                            value={formik.values.firstName}
                          />
                        </div>
                        <div
                          className="col-md-4 my-under-500"
                          ref={setFieldRef("lastName")}
                        >
                          <MuiInput
                            label="Last Name"
                            placeholder="Enter Last Name"
                            isRequired
                            isInvalid={
                              formik.touched.lastName &&
                              !!formik.errors.lastName
                            }
                            ref={setFieldRef("lastName")}
                            errorText={formik.errors.lastName as string}
                            onChange={(value) =>
                              formik.setFieldValue("lastName", value)
                            }
                            value={formik.values.lastName}
                          />
                        </div>
                        <div
                          className="col-md-4 mt-4"
                          ref={setFieldRef("email")}
                        >
                          <MuiInput
                            label="Email"
                            placeholder="Enter Email"
                            isRequired
                            isInvalid={
                              formik.touched.email && !!formik.errors.email
                            }
                            errorText={formik.errors.email as string}
                            onChange={(value) =>
                              formik.setFieldValue("email", value)
                            }
                            value={formik.values.email}
                            disabled={!!editingUser?.user?.email}
                          />
                        </div>
                        <div
                          className="col-md-4 mt-4 my-under-500"
                          ref={setFieldRef("mobileNumber")}
                        >
                          {/* <MuiInput
                            label="Mobile"
                            placeholder="Enter Mobile"
                            isRequired
                            isInvalid={
                              formik.touched.mobileNumber &&
                              !!formik.errors.mobileNumber
                            }
                            errorText={formik.errors.mobileNumber as string}
                            slotProps={{ htmlInput: { maxLength: 10 } }}
                            onChange={(value) =>
                              formik.setFieldValue("mobileNumber", value)
                            }
                            value={formik.values.mobileNumber}
                            ref={setFieldRef("mobileNumber")}
                          /> */}
                          <div className="form-group">
                            <PhoneInput
                              value={formik.values.mobileNumber}
                              placeholder="Enter mobile number"
                              onChange={(value) => {
                                const digitsOnly = value
                                  .replace(/\D/g, "")
                                  .slice(0, 15); // Adjust length as per need
                                formik.setFieldValue(
                                  "mobileNumber",
                                  digitsOnly
                                );
                              }}
                              inputStyle={{
                                width: "100%",
                                borderColor:
                                  formik.touched.mobileNumber &&
                                  formik.errors.mobileNumber
                                    ? "#d32f2f"
                                    : "#ccc",
                              }}
                              // country={"ae"}
                              preferredCountries={["ae", "in", "us"]}
                              enableSearch
                              inputProps={{
                                name: "mobile",
                                required: true,
                                onBlur: () =>
                                  formik.setFieldTouched("mobileNumber", true),
                              }}
                              isValid={(value: string) => {
                                const mobileRegex = /^[0-9]{8,15}$/;
                                const isValid = mobileRegex.test(
                                  value.replace(/^\+?\d{1,4}/, "")
                                );
                                return isValid || "";
                              }}
                            />

                            {/* Manually show Formik error below the input */}
                            {formik.touched.mobileNumber &&
                              formik.errors.mobileNumber && (
                                <span className="phone-error-msg">
                                  {formik.errors.mobileNumber}
                                </span>
                              )}
                          </div>
                        </div>
                        {/* <div className="col-md-4 mt-3">
                          <MuiDropDown
                            label="Role"
                            options={roleList}
                            placeholder="Enter Role"
                            isRequired
                            isInvalid={
                              formik.touched.role && !!formik.errors.role
                            }
                            errorText={formik.errors.role as string}
                            onChange={(value: string, id: string) => {
                              console.log(id, "....");
                              formik.setFieldValue("role", value);
                              formik.setFieldValue("roleId", id);
                            }}
                            value={formik.values.role}
                          />
                        </div> */}
                        {false && (
                          <>
                            <div className="col-md-4 mt-4">
                              <MuiDropDown
                                label="Branch"
                                options={branchList}
                                placeholder="Enter Branch"
                                isRequired={true}
                                isInvalid={
                                  formik.touched.branch &&
                                  !!formik.errors.branch
                                }
                                errorText={formik?.errors.branch as string}
                                onChange={(value: any) => {
                                  formik.setFieldValue("branch", value);
                                }}
                                onBlur={() =>
                                  formik.setFieldTouched("branch", true)
                                }
                                value={formik.values.branch}
                              />
                            </div>
                            <div className="col-md-4 mt-4">
                              <MuiDropDown
                                label="Business"
                                options={corporateList}
                                placeholder="Select Business"
                                isRequired
                                isInvalid={
                                  formik.touched.corporateName &&
                                  !!formik.errors.corporateName
                                }
                                errorText={
                                  formik.errors.corporateName as string
                                }
                                onChange={(value) =>
                                  formik.setFieldValue("corporateName", value)
                                }
                                value={formik.values.corporateName}
                              />
                            </div>
                          </>
                        )}
                        <div
                          className="col-md-4 mt-4 my-under-500"
                          ref={setFieldRef("employeeCode")}
                        >
                          <MuiInput
                            label="Employee Code"
                            placeholder="Enter Employee Code"
                            isRequired={true}
                            isInvalid={
                              formik.touched.employeeCode &&
                              !!formik.errors.employeeCode
                            }
                            errorText={formik?.errors.employeeCode as string}
                            onChange={(value) =>
                              formik.setFieldValue("employeeCode", value)
                            }
                            value={formik.values.employeeCode}
                            ref={setFieldRef("employeeCode")}
                          />
                        </div>
                        <div
                          className="col-md-4 mt-4"
                          ref={setFieldRef("status")}
                        >
                          <MuiDropDown
                            label="Status"
                            options={statusList}
                            placeholder="Select Status"
                            isRequired
                            isInvalid={
                              formik.touched.status && !!formik.errors.status
                            }
                            errorText={formik.errors.status as string}
                            onChange={(value) =>
                              formik.setFieldValue("status", value)
                            }
                            value={formik.values.status}
                          />
                        </div>

                        {/* <div className="col-md-4 mt-3">
                          <MuiInput
                            label="Alias Name"
                            placeholder="Enter Alias Name"
                            isInvalid={
                              formik.touched.aliasName &&
                              !!formik.errors.aliasName
                            }
                            errorText={formik?.errors.aliasName as string}
                            onChange={(value) =>
                              formik.setFieldValue("aliasName", value)
                            }
                            value={formik.values.aliasName}
                            disabled={!!editingUser?.email}
                          />
                        </div> */}
                        <div>
                          <Permissions
                            permissionId={getPermissionsId}
                            selectedPermissionId={formik.values.feature_ids}
                            clearPermissions={clearPermissions}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                            marginTop: "8px",
                          }}
                        >
                          <div className="user-button w-full">
                            <MuiButton
                              className="clear-btn "
                              label={editingUser ? "Back" : "Clear"}
                              type="submit"
                              onClick={() => {
                                editingUser
                                  ? navigate("/UserInfoList")
                                  : handleClear();
                              }}
                            />
                            <MuiButton
                              label={
                                editingUser ? "Update User" : "Create User"
                              }
                              className="main-btn get-btn"
                              variant="contained"
                              size="large"
                              onClick={handleFormSubmit}
                              type="submit"
                            />
                          </div>
                          {/* <MuiButton
                            label={editingUser ? "Update User" : "Create User"}
                            className="main-btn"
                            variant="contained"
                            size="large"
                            onClick={() => formik.handleSubmit()}
                          /> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default UserManagement;
